import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { formatNum, formatSgd, apiErr } from "../lib/utils";
import { ProvenanceTrigger } from "../components/provenance/ProvenanceTrigger";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  ShieldAlert, Plus, Sparkles, Target, Trash2, Library, Grid3x3, AlertTriangle,
  TrendingUp, CheckCircle2, ArrowLeft, Layers, DollarSign,
} from "lucide-react";
import { toast } from "sonner";

const BAND = {
  critical: { bg: "bg-red-500", text: "text-red-500", label: "Critical" },
  high: { bg: "bg-orange-500", text: "text-orange-500", label: "High" },
  medium: { bg: "bg-[#FFB800]", text: "text-[#FFB800]", label: "Medium" },
  low: { bg: "bg-[#00E65B]", text: "text-[#00E65B]", label: "Low" },
};
const HEAT_COLOR = (li, im) => {
  const s = li * im;
  if (s >= 20) return "bg-red-500/80";
  if (s >= 13) return "bg-orange-500/70";
  if (s >= 7) return "bg-[#FFB800]/60";
  return "bg-[#00E65B]/50";
};

function StatCard({ label, value, unit, icon: Icon, testid, tone, pkey }) {
  return (
    <div className="card-flat p-4 group" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground mb-2"><Icon className="h-4 w-4" /><span className="label-eyebrow text-[10px]">{label}</span>{pkey && <ProvenanceTrigger pkey={pkey} className="ml-auto" />}</div>
      <div className={`font-mono text-2xl font-bold tracking-tight ${tone || ""}`}>{value}<span className="text-xs text-muted-foreground font-sans ml-1">{unit}</span></div>
    </div>
  );
}

/* ----------------------------- library / add ----------------------------- */
function AddRiskDialog({ lib, activeTitles, onAdded }) {
  const [open, setOpen] = useState(false);
  const [cat, setCat] = useState("all");
  const [busy, setBusy] = useState(null);
  const items = (lib?.library || []).filter((t) => cat === "all" || t.category === cat);
  const add = async (t) => {
    setBusy(t.template_id);
    try { await api.post("/risk/risks", { template_id: t.template_id }); toast.success(`Added: ${t.title}`); onAdded(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="risk-library-btn"><Library className="h-4 w-4" /> Risk library</Button></DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="risk-library-dialog">
        <DialogHeader><DialogTitle className="font-display">Risk library</DialogTitle></DialogHeader>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-56" data-testid="risk-library-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {(lib?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="space-y-2 mt-2">
          {items.map((t) => {
            const added = activeTitles.has(t.title);
            return (
              <div key={t.template_id} className="card-flat p-3 flex items-start justify-between gap-3" data-testid={`risk-lib-${t.template_id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><Badge variant="outline" className="text-[9px]">{t.category}</Badge>{t.lane === "computed" && <Badge variant="lime" className="text-[9px]">Computed lane</Badge>}<span className="font-medium text-sm">{t.title}</span></div>
                  <div className="text-xs text-muted-foreground mt-1">{t.description}</div>
                </div>
                <Button size="sm" variant={added || t.lane === "computed" ? "ghost" : "primary"} disabled={added || busy === t.template_id || t.lane === "computed"}
                  title={t.lane === "computed" ? "Computed lane — created from hazard × exposure under Hazards & Locations" : undefined}
                  onClick={() => add(t)} data-testid={`risk-add-${t.template_id}`}>
                  {t.lane === "computed" ? "Computed only" : added ? "Added" : busy === t.template_id ? "…" : <><Plus className="h-3.5 w-3.5" /> Add</>}
                </Button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------------- detail ----------------------------- */
function RiskDetail({ riskId, lib, onBack }) {
  const [r, setR] = useState(null);
  const [aiActions, setAiActions] = useState([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [newAction, setNewAction] = useState("");

  const load = useCallback(() => { api.get(`/risk/risks/${riskId}`).then((res) => setR(res.data)).catch((e) => toast.error(apiErr(e))); }, [riskId]);
  useEffect(() => { load(); }, [load]);
  if (!r) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading risk…</div>;

  const patch = async (body) => { try { const res = await api.patch(`/risk/risks/${riskId}`, body); setR(res.data); } catch (e) { toast.error(apiErr(e)); } };
  const aiSuggest = async () => { setAiBusy(true); try { const res = await api.post(`/risk/risks/${riskId}/ai/mitigations`); setAiActions(res.data.suggested_actions); } catch (e) { toast.error(apiErr(e)); } finally { setAiBusy(false); } };
  const addAction = async (action) => { if (!action) return; try { const res = await api.post(`/risk/risks/${riskId}/mitigations`, { action }); setR(res.data); setNewAction(""); setAiActions(aiActions.filter((a) => a !== action)); } catch (e) { toast.error(apiErr(e)); } };
  const convert = async (mid) => { try { const res = await api.post(`/risk/risks/${riskId}/convert`, { mitigation_id: mid }); setR(res.data.risk); toast.success("Initiative created from mitigation"); } catch (e) { toast.error(apiErr(e)); } };

  const band = BAND[r.residual_band] || BAND.low;
  const Field = ({ label, k, type = "number", min, max }) => (
    <div><label className="label-eyebrow text-[10px]">{label}</label>
      <Input type={type} min={min} max={max} defaultValue={r[k] ?? ""} onBlur={(e) => { const v = type === "number" ? Number(e.target.value) : e.target.value; if (v !== r[k]) patch({ [k]: v }); }} /></div>
  );

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="risk-detail">
      <div>
        <button onClick={onBack} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2" data-testid="risk-back-btn"><ArrowLeft className="h-3.5 w-3.5" /> Risk register</button>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1"><Badge variant="outline">{r.category}</Badge><Badge variant="outline">{r.time_horizon}</Badge>
              <Badge variant={r.lane === "computed" ? "lime" : "outline"} className="text-[9px]" data-testid="risk-lane-badge">
                {r.lane === "computed" ? "Computed" : r.lane === "template_legacy" ? "Template (legacy)" : "Template"}
              </Badge></div>
            <h1 className="font-display text-2xl font-bold tracking-tight">{r.title}</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{r.description}</p>
          </div>
          <div className="text-right">
            <div className={`font-mono text-4xl font-bold ${band.text}`}>{r.residual_score}</div>
            <div className="text-xs text-muted-foreground">residual · {band.label}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Inherent" value={r.inherent_score} unit="/25" icon={AlertTriangle} testid="risk-inherent" tone={BAND[r.inherent_band]?.text} />
        <StatCard label="Residual" value={r.residual_score} unit="/25" icon={ShieldAlert} testid="risk-residual" tone={band.text} />
        <StatCard label="Priority" value={r.priority_score} unit="" icon={TrendingUp} testid="risk-priority" />
        <StatCard label="Financial exposure" value={formatSgd(r.financial_exposure_sgd)} unit="" icon={DollarSign} testid="risk-exposure" />
      </div>

      <Tabs defaultValue="assessment">
        <TabsList>
          <TabsTrigger value="assessment" data-testid="risk-tab-assessment">Assessment</TabsTrigger>
          <TabsTrigger value="mitigations" data-testid="risk-tab-mitigations">Mitigations</TabsTrigger>
        </TabsList>

        <TabsContent value="assessment" className="mt-4 space-y-4">
          <div className="card-flat p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><label className="label-eyebrow text-[10px]">Likelihood (1–5)</label>
              <Select value={String(r.likelihood)} onValueChange={(v) => patch({ likelihood: Number(v) })}>
                <SelectTrigger data-testid="risk-likelihood"><SelectValue /></SelectTrigger>
                <SelectContent>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><label className="label-eyebrow text-[10px]">Impact (1–5)</label>
              <Select value={String(r.impact)} onValueChange={(v) => patch({ impact: Number(v) })}>
                <SelectTrigger data-testid="risk-impact"><SelectValue /></SelectTrigger>
                <SelectContent>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><label className="label-eyebrow text-[10px]">Control effectiveness (%)</label>
              <Input type="number" min={0} max={100} defaultValue={r.control_effectiveness} data-testid="risk-control" onBlur={(e) => patch({ control_effectiveness: Number(e.target.value) })} /></div>
            <Field label="Financial materiality (1–5)" k="financial_materiality" min={1} max={5} />
            <Field label="Compliance materiality (1–5)" k="compliance_materiality" min={1} max={5} />
            <Field label="Financial exposure (S$)" k="financial_exposure_sgd" />
            <div><label className="label-eyebrow text-[10px]">Risk owner</label><Input defaultValue={r.owner || ""} onBlur={(e) => patch({ owner: e.target.value })} /></div>
            <div><label className="label-eyebrow text-[10px]">Status</label>
              <Select value={r.status} onValueChange={(v) => patch({ status: v })}>
                <SelectTrigger data-testid="risk-status"><SelectValue /></SelectTrigger>
                <SelectContent>{(lib?.statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><label className="label-eyebrow text-[10px]">Review frequency</label>
              <Select value={r.review_frequency} onValueChange={(v) => patch({ review_frequency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{(lib?.review_frequencies || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="card-flat p-4">
              <div className="label-eyebrow mb-2">Controls in place</div>
              {(r.controls || []).length ? <ul className="text-sm space-y-1">{r.controls.map((c, i) => <li key={i} className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-[#00E65B]" />{c}</li>)}</ul> : <div className="text-sm text-muted-foreground">No controls recorded.</div>}
            </div>
            <div className="card-flat p-4">
              <div className="label-eyebrow mb-2">Risk mapping</div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Material topic</span><span>{r.material_topic || "—"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Frameworks</span><span>{(r.frameworks || []).join(", ") || "—"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Facility</span><span>{r.facility || "—"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Driver</span><span>{r.driver || "—"}</span></div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="mitigations" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="label-eyebrow">Mitigation actions → initiatives</div>
            <Button size="sm" variant="primary" onClick={aiSuggest} disabled={aiBusy} data-testid="risk-ai-mitigations"><Sparkles className="h-3.5 w-3.5" /> {aiBusy ? "Thinking…" : "AI suggest"}</Button>
          </div>
          {aiActions.length > 0 && (
            <div className="card-flat p-3" data-testid="risk-ai-suggestions">
              <div className="label-eyebrow text-[10px] mb-2 inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent" /> AI-suggested actions — add the ones you want</div>
              <div className="space-y-1.5">
                {aiActions.map((a, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-sm">
                    <span>{a}</span>
                    <Button size="sm" variant="ghost" onClick={() => addAction(a)}><Plus className="h-3.5 w-3.5" /> Add</Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <Input value={newAction} onChange={(e) => setNewAction(e.target.value)} placeholder="Add a custom mitigation action…" data-testid="risk-action-input" />
            <Button variant="outline" onClick={() => addAction(newAction)} data-testid="risk-add-action">Add</Button>
          </div>
          <div className="space-y-2">
            {(r.mitigations || []).map((m) => (
              <div key={m.id} className="card-flat p-3 flex items-center justify-between gap-3" data-testid={`risk-mit-${m.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium">{m.action}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{m.owner || "Unassigned"} · {m.status}{m.initiative_id && " · linked to initiative"}</div>
                </div>
                {m.initiative_id
                  ? <Badge variant="default" className="text-[9px] shrink-0">Initiative ✓</Badge>
                  : <Button size="sm" variant="outline" onClick={() => convert(m.id)} data-testid={`risk-convert-${m.id}`}><Target className="h-3.5 w-3.5" /> To initiative</Button>}
              </div>
            ))}
            {!(r.mitigations || []).length && <div className="text-sm text-muted-foreground py-4 text-center">No mitigation actions yet. Use AI suggest or add your own.</div>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ----------------------------- register + dashboard ----------------------------- */
export default function RiskRegister() {
  const [lib, setLib] = useState(null);
  const [dash, setDash] = useState(null);
  const [risks, setRisks] = useState([]);
  const [active, setActive] = useState(null);
  const [recs, setRecs] = useState(null);
  const [recBusy, setRecBusy] = useState(false);
  const [catFilter, setCatFilter] = useState("all");

  const load = useCallback(() => {
    Promise.all([api.get("/risk/dashboard"), api.get("/risk/risks")])
      .then(([d, r]) => { setDash(d.data); setRisks(r.data); }).catch(() => setDash({ empty: true }));
  }, []);
  useEffect(() => { api.get("/risk/library").then((r) => setLib(r.data)); load(); }, [load]);

  const recommend = async () => { setRecBusy(true); try { const r = await api.post("/risk/ai/recommend"); setRecs(r.data); } catch (e) { toast.error(apiErr(e)); } finally { setRecBusy(false); } };
  const addRec = async (tid) => { try { await api.post("/risk/risks", { template_id: tid }); toast.success("Risk added"); setRecs({ ...recs, recommendations: recs.recommendations.filter((x) => x.template_id !== tid) }); load(); } catch (e) { toast.error(apiErr(e)); } };

  if (active) return <RiskDetail riskId={active} lib={lib} onBack={() => { setActive(null); load(); }} />;
  if (!dash) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading risk register…</div>;

  const activeTitles = new Set(risks.map((r) => r.title));
  const shown = risks.filter((r) => catFilter === "all" || r.category === catFilter);
  const LIK = ["1", "2", "3", "4", "5"], IMP = ["5", "4", "3", "2", "1"];

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="risk-register-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-1">Scenarios & Risks · Risk Register</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter flex items-center gap-3"><ShieldAlert className="h-8 w-8 text-accent" /> Risk Register</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">Activate ESG, climate, regulatory, supply-chain and operational risks, assess exposure, and convert AI-recommended mitigations into initiatives.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={recommend} disabled={recBusy} data-testid="risk-ai-recommend"><Sparkles className="h-4 w-4" /> {recBusy ? "Analysing…" : "AI recommend"}</Button>
          {lib && <AddRiskDialog lib={lib} activeTitles={activeTitles} onAdded={load} />}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Active risks" value={dash.total_risks || 0} unit="" icon={ShieldAlert} testid="risk-stat-total" pkey="risk.total" />
        <StatCard label="Critical / High" value={dash.critical_high || 0} unit="" icon={AlertTriangle} testid="risk-stat-high" pkey="risk.high" tone={dash.critical_high ? "text-orange-500" : ""} />
        <StatCard label="Financial exposure" value={formatNum(dash.total_exposure_sgd / 1000, { decimals: 0 })} unit="K S$" icon={DollarSign} testid="risk-stat-exposure" pkey="risk.exposure" />
        <StatCard label="Open mitigations" value={dash.open_mitigations || 0} unit="" icon={Layers} testid="risk-stat-mitigations" pkey="risk.mitigations" />
        <StatCard label="→ Initiatives" value={dash.converted_initiatives || 0} unit="" icon={Target} testid="risk-stat-initiatives" pkey="risk.initiatives" />
      </div>

      {recs && (
        <div className="card-flat p-4" data-testid="risk-recommendations">
          <div className="label-eyebrow mb-2 inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent" /> AI-recommended risks</div>
          <p className="text-sm text-muted-foreground mb-3">{recs.summary}</p>
          <div className="space-y-2">
            {(recs.recommendations || []).map((rc) => (
              <div key={rc.template_id} className="flex items-start justify-between gap-3 border-b border-border pb-2 last:border-0">
                <div><div className="text-sm font-medium"><Badge variant="outline" className="text-[9px] mr-2">{rc.category}</Badge>{rc.title}</div><div className="text-xs text-muted-foreground mt-1">{rc.reason}</div></div>
                <Button size="sm" variant="primary" onClick={() => addRec(rc.template_id)} data-testid={`risk-addrec-${rc.template_id}`}><Plus className="h-3.5 w-3.5" /> Add</Button>
              </div>
            ))}
            {!(recs.recommendations || []).length && <div className="text-sm text-muted-foreground">All recommended risks are already in your register.</div>}
          </div>
        </div>
      )}

      <Tabs defaultValue="register">
        <TabsList>
          <TabsTrigger value="register" data-testid="risk-view-register">Register</TabsTrigger>
          <TabsTrigger value="heatmap" data-testid="risk-view-heatmap">Heatmap</TabsTrigger>
          <TabsTrigger value="exposure" data-testid="risk-view-exposure">Exposure</TabsTrigger>
        </TabsList>

        {/* Register list */}
        <TabsContent value="register" className="mt-4 space-y-3">
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="w-56" data-testid="risk-cat-filter"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All categories</SelectItem>{(lib?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          {shown.sort((a, b) => b.priority_score - a.priority_score).map((r) => {
            const band = BAND[r.residual_band] || BAND.low;
            return (
              <button key={r.id} onClick={() => setActive(r.id)} className="card-flat p-4 w-full text-left hover:border-accent/60 hover:bg-surface-hover transition-colors flex items-center justify-between gap-4" data-testid={`risk-row-${r.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${band.bg}`} />
                  <div className="min-w-0">
                    <div className="font-display font-semibold">{r.title}{r.synthetic && <Badge variant="warning" className="ml-2 text-[9px]" title="Baseline-seeded example">Synthetic</Badge>}</div>
                    <div className="text-xs text-muted-foreground mt-0.5"><Badge variant="outline" className="text-[9px] mr-2">{r.category}</Badge>
                      <Badge variant={r.lane === "computed" ? "lime" : "outline"} className="text-[9px] mr-2" data-testid={`risk-lane-${r.id}`}>
                        {r.lane === "computed" ? "Computed" : r.lane === "template_legacy" ? "Template (legacy)" : "Template"}
                      </Badge>{r.owner || "Unassigned"} · {r.status}</div>
                  </div>
                </div>
                <div className="flex items-center gap-5 shrink-0 text-right">
                  <div><div className="font-mono text-sm font-bold">{r.residual_score}<span className="text-muted-foreground text-xs">/25</span></div><div className="text-[10px] text-muted-foreground">residual</div></div>
                  <div><div className="font-mono text-sm font-bold">{r.priority_score}</div><div className="text-[10px] text-muted-foreground">priority</div></div>
                  <Badge variant="outline" className={`text-[10px] ${band.text}`}>{band.label}</Badge>
                </div>
              </button>
            );
          })}
          {!shown.length && <div className="card-flat p-10 text-center text-muted-foreground">No risks in this view. Use the risk library or AI recommend to add risks.</div>}
        </TabsContent>

        {/* Heatmap */}
        <TabsContent value="heatmap" className="mt-4">
          <div className="card-flat p-6">
            <div className="label-eyebrow mb-4 inline-flex items-center gap-1"><Grid3x3 className="h-3.5 w-3.5" /> Likelihood × Impact heatmap (inherent)</div>
            <div className="flex">
              <div className="flex flex-col justify-around mr-2 text-[10px] text-muted-foreground font-mono"><span className="rotate-180 [writing-mode:vertical-rl] self-center">Impact →</span></div>
              <div>
                <div className="grid grid-cols-5 gap-1.5" data-testid="risk-heatmap">
                  {IMP.map((im) => LIK.map((li) => {
                    const count = dash.heatmap[5 - Number(im)][Number(li) - 1];
                    return (
                      <div key={`${im}-${li}`} className={`h-16 w-16 rounded-sm flex items-center justify-center font-mono text-lg font-bold ${HEAT_COLOR(Number(li), Number(im))} ${count ? "text-black" : "text-black/30"}`}>
                        {count || ""}
                      </div>
                    );
                  }))}
                </div>
                <div className="grid grid-cols-5 gap-1.5 mt-1 text-[10px] text-muted-foreground font-mono text-center">{LIK.map((l) => <span key={l}>{l}</span>)}</div>
                <div className="text-[10px] text-muted-foreground font-mono text-center mt-1">Likelihood →</div>
              </div>
            </div>
            <div className="flex gap-4 mt-4 text-xs">
              {Object.entries(BAND).reverse().map(([k, v]) => <span key={k} className="flex items-center gap-1.5"><span className={`h-3 w-3 rounded-sm ${v.bg}`} />{v.label}</span>)}
            </div>
          </div>
        </TabsContent>

        {/* Exposure */}
        <TabsContent value="exposure" className="mt-4 space-y-3">
          <div className="card-flat p-4">
            <div className="label-eyebrow mb-3">Risk count by category</div>
            <div className="space-y-2">
              {Object.entries(dash.by_category).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]).map(([c, n]) => (
                <div key={c}>
                  <div className="flex justify-between text-xs mb-1"><span>{c}</span><span className="font-mono">{n}</span></div>
                  <Progress value={(n / dash.total_risks) * 100} className="h-1.5" />
                </div>
              ))}
            </div>
          </div>
          <div className="card-flat p-4">
            <div className="label-eyebrow mb-3">Severity distribution (residual)</div>
            <div className="grid grid-cols-4 gap-3">
              {["critical", "high", "medium", "low"].map((b) => (
                <div key={b} className="text-center border border-border rounded-sm p-3">
                  <div className={`font-mono text-2xl font-bold ${BAND[b].text}`}>{dash.by_band[b]}</div>
                  <div className="text-[10px] text-muted-foreground">{BAND[b].label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="card-flat p-4">
            <div className="label-eyebrow mb-2">Top 5 priority risks (board summary)</div>
            <div className="space-y-1.5">
              {dash.top_risks.map((t) => (
                <div key={t.id} className="flex items-center justify-between text-sm border-b border-border pb-1.5 last:border-0">
                  <span className="flex items-center gap-2"><span className={`h-2 w-2 rounded-full ${BAND[t.residual_band].bg}`} />{t.title}</span>
                  <span className="font-mono text-xs">{t.priority_score} · {t.owner || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
